// Place Order Requirements:
// The user must complete all checkout steps with valid data before placing the order.

// The place order process involves the following steps, which must occur in this order:
// 1. Create an account: Requires contact info and password.
// 2. Create a business entity: Requires business information and the account UUID from step 1.
// 2.5 Update the cart with the business UUID: Requires the account UUID and the business entity UUID from previous steps.
// 3. Start the place order submission process: Requires the account UUID from step 1 and the card token.
// This step is critical and cannot be skipped (as previous steps). If a payment error occurs, it can be retried.
// The status of this process will be monitored in the parent component using the useSubmitOrder hook.

// These steps are executed sequentially. In case of a retry, successful steps are skipped. Converting the cart to an order is the most likely step to fail.

import { useEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCreateBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { useCart } from '@zenbusiness/application-commons-ecommerce';
import { useCreateAccount } from '@zenbusiness/application-commons-identity';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import { getBusinessNameWithIndicator } from '#client/utils/businessInfo';

import { CHECKOUT } from '../constants';
import { useCheckoutContext } from './useCheckoutContext';

const { ACTION } = CHECKOUT;

/**
 * Hook that manages the place order process.
 * @param {object} options
 * @param {Function} options.submitOrder Function to submit the order.
 * @returns {Function} Function to place an order.
 */
export function usePlaceOrder({ submitOrder }) {
  const captureException = useCaptureException();
  const [{ password, cardToken, paymentMethodId }, dispatch] = useCheckoutContext();
  const { contactInfo, businessInfo, get, set } = useFlowState();
  const { hasFormation } = useFlowConfig();
  const { create: createAccount, errors: accountErrors } = useCreateAccount();
  const { create: createBusiness, errors: businessErrors } = useCreateBusinessEntity();
  const { errors: cartErrors } = useCart();
  const { accountUuid: activeAccountUuid } = useAuthentication();
  const trackAccountCreated = useClickstreamCallback('accountCreated');

  const storedAccountUuid = get('accountUuid');

  // Get the first error from each step
  const accountError = accountErrors[0];
  const businessError = businessErrors[0];
  const cartError = cartErrors[0];

  // Sync error to the context
  useEffect(() => {
    const fatalError = businessError || cartError;
    if (fatalError) {
      captureException(fatalError);
      dispatch({
        type: ACTION.PLACE_ORDER_ERROR,
        payload: { message: fatalError.message, isFatal: true }
      });
    } else if (accountError) {
      captureException(accountError);
      dispatch({
        type: ACTION.PLACE_ORDER_ERROR,
        payload: { message: accountError.message, isFatal: false }
      });
    }
  }, [captureException, accountError, businessError, cartError, dispatch]);

  // TODO: this cardTokenParam is to allow Apple pay button to pass it directly to this function.
  // once the Apple pay button is refactored, this might change.
  return async function placeOrder(cardTokenParam) {
    try {
      dispatch({ type: ACTION.PLACE_ORDER_START });
      let accountUuid = activeAccountUuid || storedAccountUuid || null;
      let businessEntityUuid = businessInfo.businessEntityUuid || null;

      // 1. Create account if not already created
      if (!accountUuid) {
        const accountDetails = {
          ...contactInfo,
          password
        };
        accountUuid = await createAccount(accountDetails);
        if (!accountUuid) {
          return null; /* return early if account creation fails */
        }
        trackAccountCreated();
        set('accountUuid', accountUuid);
      }

      // 2. Create business entity
      if (!businessEntityUuid) {
        const businessDetails = {
          accountUuid,
          jurisdictionCode: businessInfo.jurisdictionState,
          name: getBusinessNameWithIndicator(businessInfo.name, businessInfo.companyIndicator),
          type: businessInfo.entityType,
          formationRequired: hasFormation
        };
        businessEntityUuid = await createBusiness(businessDetails);
        if (!businessEntityUuid) {
          return null; /* return early if business creation fails */
        }

        set('businessInfo.businessEntityUuid', businessEntityUuid);
      }

      // 3. Start the place order submission process
      const orderUuid = await submitOrder({
        accountUuid,
        businessEntityUuid,
        // this function can also receives the cardTokenParam directly
        cardToken: cardTokenParam || cardToken,
        paymentMethodId
      });
      if (!orderUuid) {
        return null; /* return early if order submission fails */
      }

      return orderUuid;
    } catch (error) {
      // TODO: Clean this up once the error standardization epic is implemented,
      // The error should be an object, not an array
      const firstError = Array.isArray(error) ? error[0] : error;
      const { message: errorMessage, extensions: { errorCode } = {} } = firstError || {};

      // Send any error when placing orders to the context
      // TODO: isFatal should be determined by the error we get back
      captureException(firstError);

      dispatch({
        type: ACTION.PLACE_ORDER_ERROR,
        payload: { message: errorMessage, isFatal: false, errorCode }
      });
    }
    return null;
  };
}
